import React, { useState, useEffect, useCallback } from 'react';
import '@/App.css';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import GameLobby from '@/components/GameLobby';
import GameScreen from '@/components/GameScreen';
import Login from '@/pages/Login';
import SignUp from '@/pages/SignUp';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AdTest from '@/pages/AdTest';
import VerifyEmail from '@/pages/VerifyEmail';
import Invite from '@/pages/Invite';
import AAMissileRotationTest from '@/components/AAMissileRotationTest';
import BufferTestPage from '@/pages/BufferTestPage';
import ReportingPage from '@/pages/ReportingPage';
import Tutorial from '@/pages/Tutorial';
import AdSenseModal from '@/components/AdSenseModal';
import MobileWarningBanner from '@/components/MobileWarningBanner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';

// TEST MODE: Set to true to render minimal test cube
const TEST_MODE = false;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Main App Content (needs to be inside AuthProvider to use useAuth)
function AppContent() {
  const { session, user } = useAuth(); // Get session for auth token
  const [screen, setScreen] = useState('lobby');
  const [game, setGame] = useState(null);
  const [existingGames, setExistingGames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isLoadedGame, setIsLoadedGame] = useState(false); // Track if game was loaded vs newly created
  const [showAdModal, setShowAdModal] = useState(false);
  const [shouldShowAd, setShouldShowAd] = useState(false);

  // Fix for iOS landscape - ensure scrolling to top on orientation change
  useEffect(() => {
    const handleOrientationChange = () => {
      // Small delay to let layout settle
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }, 100);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  // Log device type once per session for admin analytics (mobile vs desktop)
  useEffect(() => {
    if (sessionStorage.getItem('device_visit_logged')) return;
    try {
      const ua = navigator.userAgent || '';
      let deviceType = 'desktop';
      if (/iPad|Android(?!.*mobile)/i.test(ua)) deviceType = 'tablet';
      else if (/iPhone|Android.*mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) deviceType = 'phone';
      fetch(`${BACKEND_URL}/api/analytics/device-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_type: deviceType,
          width: window.innerWidth,
          height: window.innerHeight
        })
      }).then(() => sessionStorage.setItem('device_visit_logged', '1'))
        .catch(() => {});
    } catch (e) {
      // fire-and-forget; never break the app over analytics
    }
  }, []);

  // 📱 iOS / mobile audio unlock — registers listeners so the first
  // user gesture (tap/click) unlocks programmatic `new Audio().play()`
  // calls for the rest of the session. Without this, airstrike sirens,
  // ejection screams, deploy sounds, etc. silently fail on iPhone.
  useEffect(() => {
    import('@/utils/sounds').then(({ unlockAudioOnFirstGesture }) => {
      if (typeof unlockAudioOnFirstGesture === 'function') {
        unlockAudioOnFirstGesture();
      }
    });
  }, []);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    if (session?.access_token) {
      return { Authorization: `Bearer ${session.access_token}` };
    }
    return {};
  };

  // Fetch existing games on mount and when session changes
  useEffect(() => {
    if (session) {
      fetchGames();
    }
  }, [session]);

  const fetchGames = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/games`, {
        headers
      });
      setExistingGames(response.data);
    } catch (error) {
      console.error('Failed to fetch games:', error);
    }
  };

  const startNewGame = async (name, playerCountry) => {
    setIsLoading(true);
    try {
      const headers = getAuthHeaders();
      
      const response = await axios.post(`${API}/games`, {
        name,
        player_country: playerCountry
      }, {
        headers
      });
      
      setGame(response.data);
      setIsLoadedGame(false); // Mark as new game (show splash/intro)
      setScreen('game');
      showNotification(`Operation ${name} initiated. You control ${playerCountry}.`, 'success');
    } catch (error) {
      console.error('Failed to start game:', error);
      showNotification('Failed to start game', 'danger');
    }
    setIsLoading(false);
  };

  const loadGame = async (gameId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/games/${gameId}`);
      const loadedGame = response.data;
      const playerCountry = loadedGame.countries?.find(c => c.is_player);
      
      setGame(loadedGame);
      setIsLoadedGame(true); // Mark as loaded game (skip splash/intro)
      setScreen('game');
      
      // Clear any stale notifications
      setNotification(null);
      
      // Check if it's the player's turn or if we need to process AI turns
      const actionsRemaining = playerCountry?.actions_remaining || 0;
      
      if (actionsRemaining > 0) {
        // Player's turn
        showNotification(`Game loaded! Turn ${loadedGame.current_turn} - You have ${actionsRemaining} ${actionsRemaining === 1 ? 'action' : 'actions'}`, 'success');
      } else {
        // Player has no actions - likely AI's turn
        // The notification will be handled by the GameScreen component automatically
        showNotification(`Game loaded! Turn ${loadedGame.current_turn}`, 'info');
      }
    } catch (error) {
      console.error('Failed to load game:', error);
      showNotification('Failed to load game', 'danger');
    }
    setIsLoading(false);
  };

  const deleteGame = async (gameId) => {
    try {
      await axios.delete(`${API}/games/${gameId}`);
      fetchGames();
      showNotification('Operation terminated', 'warning');
    } catch (error) {
      console.error('Failed to delete game:', error);
    }
  };

  // Notification helper - MUST be defined before performAction
  const showNotification = useCallback((message, type = 'info') => {
    if (!message || message.trim() === '') return; // Don't show empty notifications
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000); // Set to null instead of empty message
  }, []);

  const performAction = useCallback(async (actionType, targetCountry) => {
    // ✅ NULL SAFETY: Check game AND game.countries exist
    if (!game || !game.countries) return null;
    
    const playerCountry = game.countries.find(c => c.is_player);
    if (!playerCountry) return null;

    try {
      const response = await axios.post(`${API}/games/action`, {
        game_id: game.id,
        action_type: actionType,
        source_country: playerCountry.code,
        target_country: targetCountry,
        amount: 10
      });
      
      // Update game state with new data (includes updated actions_remaining)
      setGame(response.data.game);
      
      // Debug logging - ✅ NULL SAFETY
      if (response.data.game && response.data.game.countries) {
        const updatedPlayerCountry = response.data.game.countries.find(c => c.is_player);
        console.log(`[DEBUG] Action performed. Actions remaining: ${updatedPlayerCountry?.actions_remaining || 0}`);
      }
      if (response.data.result.turn_changed) {
        console.log(`[DEBUG] Turn changed! Next country: ${response.data.result.next_country}`);
      }
      
      // CRITICAL FIX: Suppress notification for air_strike - handled by animation logic
      if (actionType !== 'air_strike') {
        showNotification(response.data.result.message, actionType === 'nuclear_strike' ? 'danger' : response.data.result.success ? 'success' : 'warning');
      }
      
      return response.data.result; // Return result for animations
    } catch (error) {
      console.error('Action failed:', error);
      showNotification(error.response?.data?.detail || 'Action failed', 'danger');
      return null;
    }
  }, [game, showNotification]);

  // Note: End turn logic has been moved to GameScreen's handleEndTurn for proper state access
  const endTurn = useCallback(async () => {
    // This is a placeholder - actual logic is in GameScreen.handleEndTurn
    console.log('App.endTurn called (no-op, logic moved to GameScreen)');
  }, []);

  const backToLobby = async (gameCompleted = false) => {
    // Only record game completion if the game actually ended (victory/defeat), not manual exit
    if (gameCompleted) {
      try {
        const response = await fetch(`${API}/game-limits/record-game`, {
          method: 'POST',
          headers: {
            'X-User-Email': user?.email || 'guest@example.com'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('✅ Game completion recorded:', data);

          // Show ads if user hit the limit (2 games completed)
          if (data.show_ads) {
            setShowAdModal(true);
            // Ad modal will call actuallyReturnToLobby() when closed
            return;
          }
        }
      } catch (error) {
        console.error('❌ Error recording game completion:', error);
      }
    } else {
      console.log('⏭️ Returning to lobby (game not completed, no record)');
    }

    // If no ad, return to lobby immediately
    actuallyReturnToLobby();
  };

  const actuallyReturnToLobby = () => {
    setScreen('lobby');
    setGame(null);
    fetchGames();
    setShowAdModal(false);
  };

  return (
    <div className="App">
      {screen === 'lobby' ? (
        <GameLobby
          onStartGame={startNewGame}
          existingGames={existingGames}
          onLoadGame={loadGame}
          onDeleteGame={deleteGame}
          isLoading={isLoading}
        />
      ) : (
        <GameScreen
          game={game}
          setGame={setGame}
          onAction={performAction}
          onEndTurn={endTurn}
          onBackToLobby={backToLobby}
          notification={notification}
          setNotification={setNotification}
          showNotification={showNotification}
          isLoadedGame={isLoadedGame}
        />
      )}
      
      {/* AdSense Modal - Show after game if needed */}
      {showAdModal && (
        <AdSenseModal onClose={actuallyReturnToLobby} autoCloseDelay={5000} />
      )}
    </div>
  );
}

// Main App wrapper with AuthProvider
function App() {
  return (
    <AuthProvider>
      <Router>
        {/* Mobile device warning - shows on phones/tablets */}
        <MobileWarningBanner />
        
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite" element={<Invite />} />
          <Route path="/ad-test" element={<AdTest />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/test-missile" element={<AAMissileRotationTest />} />
          <Route path="/buffer" element={<BufferTestPage />} />
          <Route path="/reporting" element={<ReportingPage />} />
          <Route path="/tutorial" element={<Tutorial />} />
          <Route path="/" element={<AppContent />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
